// Positions.jsx — Main coordinator for the Positions page
// Handles: state, API calls, polling for live ticks, filtering, sorting
// Passes computed data to PositionsTable for display
// Built with Tailwind CSS (all new pages use Tailwind per styling decision 19 March 2026)

import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { getTheme, getDensity } from './theme';
import { fmtNum, fmtINR } from './calculations';
import { computePositionRow } from './positionsCalculations';
import { useFrozenSort } from './useFrozenSort';
import { downloadRowsAsCSV } from './csvExport';
import PositionsTable, { POS_COLUMNS } from './PositionsTable';
import useColumnLayout from './useColumnLayout';
import { ColumnsButton } from './ColumnsPanel';
import PositionsTradePopup from './PositionsTradePopup';
import OrderPopup from './OrderPopup';

const API_BASE = '/api';

export default function Positions({ demoMode, sessionId, brokerAuthenticated, user }) {
  // ─── Theme ─────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => localStorage.getItem('pos_theme') || 'dark');
  // Per-user column layout (order / width / hidden) — persisted server-side so it
  // follows the user across devices. See useColumnLayout.js.
  const cols = useColumnLayout('positions_columns', POS_COLUMNS);
  const isDark = theme === 'dark';
  const t = getTheme(isDark);

  // ─── Data ──────────────────────────────────────────────────
  const [positions, setPositions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [ticks, setTicks] = useState({});
  const [margins, setMargins] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  // Session 46 — active orders polled every 30s. Aggregated across all
  // user-accessible accounts (Kite + Kotak). Used to render orange order badges
  // next to the Script column for positions whose underlying also has an active order.
  const [myOrders, setMyOrders] = useState([]);

  // ─── Filters (arrays = multi-select, empty array = show all) ───
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [bsFilter, setBsFilter] = useState([]);
  const [accountFilter, setAccountFilter] = useState([]);
  const [sectorFilter, setSectorFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [publicPrivateFilter, setPublicPrivateFilter] = useState([]);
  const [expiryFilter, setExpiryFilter] = useState([]);
  const [oiMin, setOiMin] = useState('');
  const [volMin, setVolMin] = useState('');
  const [resultDateFrom, setResultDateFrom] = useState('');
  const [resultDateTo, setResultDateTo] = useState('');

  // ─── Sort ──────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState('underlying');
  const [sortDir, setSortDir] = useState('asc');
  // Sort-on-demand: row values update every second but row ORDER stays frozen.
  // Sorting only happens when: user clicks Re-sort, changes sort column/direction, or every 60s auto.
  const [sortVersion, setSortVersion] = useState(0);
  const autoSortTimerRef = useRef(null);

  // ─── Trade Popup (choice: Add More / Square Off) ────────────
  const [tradePopup, setTradePopup] = useState(null);

  // ─── Order Popup (actual order entry — shared with MW) ──────
  const [orderPopupRow, setOrderPopupRow] = useState(null);
  const [orderPopupMode, setOrderPopupMode] = useState('new'); // 'addMore' or 'squareOff'
  const [orderPopupDirection, setOrderPopupDirection] = useState(null); // 'BUY' or 'SELL'
  const [orderLots, setOrderLots] = useState(1);
  const [orderPrice, setOrderPrice] = useState('');
  const [orderMarginData, setOrderMarginData] = useState(null);
  const [orderAccount, setOrderAccount] = useState('');
  const [accountMargins, setAccountMargins] = useState({});

  // ─── Polling refs ──────────────────────────────────────────
  const eventSourceRef = useRef(null);     // SSE connection for live ticks + position data
  const sseReconnectTimer = useRef(null);  // Controlled reconnect (no browser auto-reconnect)
  const sseReconnectCount = useRef(0);

  // ═══════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    fetchPositions(); // initial load — SSE positions event will keep it updated
    connectSSE();     // connect once — backend handles token updates automatically
    // Auto re-sort every 60 seconds so rows don't stay frozen forever
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);

    return () => {
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
      if (sseReconnectTimer.current) { clearTimeout(sseReconnectTimer.current); sseReconnectTimer.current = null; }
    };
  }, []);

  function connectSSE() {
    // Connect once on mount — no token list needed in URL.
    // Backend sends initial positions + ticks via SSE events.
    // Backend auto-updates token list when positions change (broker poll every 60s).

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (sseReconnectTimer.current) {
      clearTimeout(sseReconnectTimer.current);
      sseReconnectTimer.current = null;
    }

    const es = new EventSource(`${API_BASE}/positions/sse?sid=${sessionId || ''}`);
    eventSourceRef.current = es;

    // Snapshot — initial tick data for all position tokens
    es.addEventListener('snapshot', (e) => {
      try {
        const data = JSON.parse(e.data);
        setTicks(data.ticks || {});
      } catch (err) { console.error('[Positions SSE] Snapshot parse error:', err); }
    });

    // Ticks — only changed ticks, pushed every 1 second
    es.addEventListener('ticks', (e) => {
      try {
        const data = JSON.parse(e.data);
        const changed = data.ticks || [];
        if (changed.length === 0) return;
        setTicks(prev => {
          const next = { ...prev };
          changed.forEach(t => {
            next[t.token] = { ...(next[t.token] || {}), ltp: t.ltp, volume: t.volume, oi: t.oi,
              depth: { buy: [{ price: t.bestBuyPrice, quantity: t.bestBuyQty }],
                       sell: [{ price: t.bestSellPrice, quantity: t.bestSellQty }] } };
          });
          return next;
        });
      } catch (err) { console.error('[Positions SSE] Ticks parse error:', err); }
    });

    // Positions — pushed by backend when broker data changes (every 60s check, only on change)
    es.addEventListener('positions', (e) => {
      try {
        const data = JSON.parse(e.data);
        setPositions(data.positions || []);
        setAccounts(data.accounts || []);
        setLastFetched(data.fetchedAt);
        setLoading(false);
        setError(null);
        sseReconnectCount.current = 0; // Connected successfully
      } catch (err) { console.error('[Positions SSE] Positions parse error:', err); }
    });

    es.onerror = () => {
      console.warn('[Positions SSE] Connection error');
      es.close();
      eventSourceRef.current = null;
      const maxRetries = 15;
      if (sseReconnectCount.current < maxRetries) {
        const delay = Math.min(3000 * Math.pow(2, sseReconnectCount.current), 30000);
        sseReconnectCount.current++;
        sseReconnectTimer.current = setTimeout(() => { sseReconnectTimer.current = null; connectSSE(); }, delay);
      }
    };
  }

  async function fetchPositions() {
    try {
      const res = await axios.get(`${API_BASE}/positions`);
      setPositions(res.data.positions || []);
      setAccounts(res.data.accounts || []);
      setLastFetched(res.data.fetchedAt);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('[Positions] Failed to fetch:', err.message);
      if (positions.length === 0) {
        setError('Could not load positions. Is the backend running and are you logged in?');
      }
      setLoading(false);
    }
  }


  async function pollMargins() {
    try {
      const res = await axios.get(`${API_BASE}/margins/all`);
      const raw = res.data.margins || res.data || {};
      const remapped = {};
      Object.entries(raw).forEach(([key, data]) => {
        if (data) {
          const tsym = key.includes(':') ? key.split(':')[1] : key;
          remapped[tsym] = { totalMargin: data.total || 0 };
        }
      });
      setMargins(remapped);
    } catch (err) { /* silent */ }
  }

  // Fetch margins once on first load (positions need margin data for columns 15-16)
  useEffect(() => { pollMargins(); }, []);

  // Fetch account margins (for order popup account selector)
  useEffect(() => {
    pollAccountMargins();
    const interval = setInterval(pollAccountMargins, 30000);
    return () => clearInterval(interval);
  }, []);

  async function pollAccountMargins() {
    try {
      const res = await axios.get(`${API_BASE}/accounts/margins`);
      setAccountMargins(res.data || {});
    } catch (err) { /* silent */ }
  }

  // Session 46 — Poll active orders so PositionsTable can render an order badge
  // next to positions whose underlying also has an active order placed (any account).
  // Polling at 30s aligns with MW's order poll, lightweight payload, no impact on
  // existing SSE flow.
  async function pollMyOrders() {
    try {
      const res = await axios.get(`${API_BASE}/orders`);
      const all = res.data.orders || res.data || [];
      const active = (Array.isArray(all) ? all : []).filter(o => {
        const s = (o.status || '').toUpperCase();
        return s === 'OPEN' || s === 'PENDING' || s === 'PARTIAL' || s === 'AMO' || s === 'CANCELLING';
      });
      setMyOrders(active);
    } catch (err) { /* silent — badge just disappears if endpoint hiccups */ }
  }
  useEffect(() => {
    pollMyOrders();
    const ordTimer = setInterval(pollMyOrders, 30000);
    return () => clearInterval(ordTimer);
  }, []);

  // Build symbol → orders[] map, memoized so it only recomputes when polled data changes.
  const ordersBySymbol = useMemo(() => {
    const map = new Map();
    for (const o of myOrders) {
      const sym = (o.symbol || o.underlying || '').toUpperCase();
      if (!sym) continue;
      const direction = (o.transactionType || o.buyOrSell || '').toUpperCase().startsWith('B') ? 'B' : 'S';
      const badge = {
        direction, strike: o.strike, type: o.type || o.optionType || '',
        accountLabel: o.accountLabel || o.account || '',
        expiryStr: o.expiryStr || o.expiry || ''
      };
      if (!map.has(sym)) map.set(sym, []);
      map.get(sym).push(badge);
    }
    return map;
  }, [myOrders]);

  // ═══════════════════════════════════════════════════════════
  // COMPUTE ROWS + FILTER + SORT
  // ═══════════════════════════════════════════════════════════

  // Unique values for filter dropdowns (used by CheckboxDropdown in column headers)
  const uniqueExpiries = useMemo(() => ['All', ...new Set(positions.map(p => p.expiryStr))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)), [positions]);
  const uniqueSectors = useMemo(() => ['All', ...[...new Set(positions.map(p => p.sector).filter(Boolean))].sort()], [positions]);
  const uniqueCategories = useMemo(() => ['All', ...[...new Set(positions.map(p => p.category).filter(Boolean))].sort()], [positions]);
  const uniquePublicPrivate = useMemo(() => ['All', ...[...new Set(positions.map(p => p.publicPrivate).filter(Boolean))].sort()], [positions]);
  const uniqueAccounts = useMemo(() => accounts.map(a => ({ id: a.id, label: a.label })), [accounts]);
  const uniqueAccountLabels = useMemo(() => ['All', ...accounts.map(a => a.label)], [accounts]);
  // Account filter uses labels for display but we need to map back to IDs for filtering
  const accountLabelToId = useMemo(() => {
    const map = {};
    accounts.forEach(a => { map[a.label] = a.id; });
    return map;
  }, [accounts]);

  // STEP 1: COMPUTE + FILTER — runs every time ticks/positions/filters change.
  // Values update live but row ORDER stays frozen.
  const filteredRows = useMemo(() => {
    // Step 1: compute all rows with live data
    let computed = positions.map(p => computePositionRow(p, ticks, margins));

    // Step 2: filter
    if (search.trim()) {
      const terms = search.toLowerCase().split(/\s+/);
      computed = computed.filter(row => {
        const haystack = `${row.symbol} ${row.underlying} ${row.type} ${row.strike} ${row.expiryStr} ${row.accountLabel}`.toLowerCase();
        return terms.every(term => haystack.includes(term));
      });
    }
    if (typeFilter.length > 0) computed = computed.filter(r => typeFilter.includes(r.type));
    if (bsFilter.length > 0) computed = computed.filter(r => bsFilter.includes(r.buyOrSell));
    if (accountFilter.length > 0) {
      const selectedIds = accountFilter.map(label => accountLabelToId[label] || label);
      computed = computed.filter(r => selectedIds.includes(r.account));
    }
    if (publicPrivateFilter.length > 0) computed = computed.filter(r => publicPrivateFilter.includes(r.publicPrivate));
    if (sectorFilter.length > 0) computed = computed.filter(r => sectorFilter.includes(r.sector));
    if (categoryFilter.length > 0) computed = computed.filter(r => categoryFilter.includes(r.category));
    if (expiryFilter.length > 0) computed = computed.filter(r => expiryFilter.includes(r.expiryStr));
    const minOI = parseInt(oiMin); if (!isNaN(minOI)) computed = computed.filter(r => r.oi >= minOI);
    const minVol = parseInt(volMin); if (!isNaN(minVol)) computed = computed.filter(r => r.volume >= minVol);
    if (resultDateFrom) computed = computed.filter(r => r.resultDateISO && r.resultDateISO >= resultDateFrom);
    if (resultDateTo) computed = computed.filter(r => r.resultDateISO && r.resultDateISO <= resultDateTo);

    return computed;
  }, [positions, ticks, margins, search, typeFilter, bsFilter, accountFilter, publicPrivateFilter, sectorFilter, categoryFilter, expiryFilter, oiMin, volMin, resultDateFrom, resultDateTo, accountLabelToId]);

  // STEP 2: SORT — frozen order (shared useFrozenSort). Order changes only on a sort change, the
  // 60s timer / manual Re-sort (sortVersion), or a filter/SET change; per-second updates refresh
  // values in place so rows hold position for the full 60s.
  const membershipKey = `${search}|${typeFilter}|${bsFilter}|${accountFilter}|${publicPrivateFilter}|${sectorFilter}|${categoryFilter}|${expiryFilter}|${oiMin}|${volMin}|${resultDateFrom}|${resultDateTo}`;
  const rows = useFrozenSort(
    filteredRows, sortCol, sortDir, sortVersion, membershipKey,
    (r) => r.id,
    { resultDate: 'resultDateISO' }
  );

  // ═══════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    // Changing sort column/direction triggers an immediate re-sort
    setSortVersion(v => v + 1);
    // Reset the 60-second auto timer so next auto re-sort is 60s from now
    if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);
  }

  function handleTradeClick(row) {
    setTradePopup(row);
  }

  // ─── Add More: open OrderPopup with 1 lot, LTP, margin fetch ──
  async function handleAddMore(row) {
    setTradePopup(null); // close choice popup
    setOrderPopupMode('addMore');
    setOrderPopupDirection(row.buyOrSell);
    setOrderLots(1);
    setOrderPrice(row.ltp > 0 ? String(row.ltp) : '');
    setOrderMarginData(null);
    // Default account to the one holding the position
    const acctId = row.account || Object.keys(accountMargins)[0] || '';
    if (!orderAccount) setOrderAccount(acctId);
    setOrderPopupRow(row);
    // Fetch margin for 1 lot
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: acctId, tradingsymbol: row.tradingsymbol, exchange: 'NFO',
        transaction_type: 'SELL', quantity: row.lotSize, price: row.ltp
      });
      setOrderMarginData(res.data);
    } catch (err) { console.error('[Positions] Add More margin fetch failed:', err.message); }
  }

  // ─── Square Off: open OrderPopup with position qty, LTP, no margin ──
  function handleSquareOff(row) {
    setTradePopup(null); // close choice popup
    setOrderPopupMode('squareOff');
    setOrderPopupDirection(row.buyOrSell);
    setOrderLots(row.lots || 1);
    setOrderPrice(row.ltp > 0 ? String(row.ltp) : '');
    setOrderMarginData(null); // not needed for square off
    const acctId = row.account || Object.keys(accountMargins)[0] || '';
    setOrderAccount(acctId);
    setOrderPopupRow(row);
  }

  // ─── Refetch margin when lots/price change in OrderPopup (Add More only) ──
  async function refetchOrderMargin(lots, price) {
    if (!orderPopupRow || !orderAccount || orderPopupMode === 'squareOff') return;
    setOrderMarginData(null);
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: orderAccount, tradingsymbol: orderPopupRow.tradingsymbol, exchange: 'NFO',
        transaction_type: 'SELL', quantity: (lots || 1) * (orderPopupRow.lotSize || 1), price: price || orderPopupRow.ltp
      });
      setOrderMarginData(res.data);
    } catch (err) { console.error('[Positions] Margin refetch failed:', err.message); }
  }

  // ─── Account change in OrderPopup ──
  async function onOrderAccountChange(newAccountId) {
    setOrderAccount(newAccountId);
    if (!orderPopupRow || orderPopupMode === 'squareOff') return;
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: newAccountId, tradingsymbol: orderPopupRow.tradingsymbol, exchange: 'NFO',
        transaction_type: 'SELL', quantity: orderPopupRow.lotSize, price: orderPopupRow.ltp
      });
      setOrderMarginData(res.data);
    } catch (err) { console.error('[Positions] Account change margin fetch failed:', err.message); }
  }

  // ═══════════════════════════════════════════════════════════
  // SUMMARY BAR — totals across all visible positions
  // ═══════════════════════════════════════════════════════════

  const summary = useMemo(() => {
    let totalPremium = 0, totalRemaining = 0, totalProfit = 0, totalStandaloneMargin = 0;
    rows.forEach(r => {
      totalPremium += r.totalPremium;
      totalRemaining += r.remaining;
      totalProfit += r.profit;
      totalStandaloneMargin += r.totalMargin;
    });
    // Actual margin used from broker accounts (includes hedge benefits)
    let brokerMarginUsed = 0;
    Object.values(accountMargins).forEach(acct => {
      if (acct && !acct.error) brokerMarginUsed += (acct.used || 0);
    });
    return { totalPremium, totalRemaining, totalProfit, totalStandaloneMargin, brokerMarginUsed, count: rows.length };
  }, [rows, accountMargins]);

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  const selectClass = `px-1.5 py-1 text-xs rounded border cursor-pointer outline-none ${isDark ? 'bg-app-input border-app-border-input text-app-text-muted' : 'bg-gray-100 border-gray-300 text-gray-600'}`;
  const inputClass = `px-2 py-1 text-xs rounded border outline-none ${isDark ? 'bg-app-input border-app-border-input text-app-text' : 'bg-gray-100 border-gray-300 text-gray-900'}`;

  // No broker connected — show message before loading anything
  if (!brokerAuthenticated && !demoMode && positions.length === 0) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Positions</div>
          <div className="text-sm text-yellow-400 mb-2">No broker account connected</div>
          <div className={`text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Use the "Broker Login" button in the navbar to connect a Kite account.</div>
        </div>
      </div>
    );
  }

  // Loading state — check if broker is not logged in
  if (loading && positions.length === 0) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Positions</div>
          <div className={`text-sm ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Loading positions...</div>
        </div>
      </div>
    );
  }

  // Error state — show broker login message if auth error
  if (error && positions.length === 0) {
    const isBrokerError = error.includes('401') || error.includes('authenticated') || error.includes('login');
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Positions</div>
          {isBrokerError ? (
            <>
              <div className="text-sm text-yellow-400 mb-2">No broker account connected</div>
              <div className={`text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Use the "Broker Login" button in the navbar to connect a Kite account.</div>
            </>
          ) : (
            <>
              <div className="text-sm text-accent-red mb-3">{error}</div>
              <button onClick={fetchPositions} className="px-4 py-1.5 text-xs font-semibold bg-accent-blue text-white rounded border-none cursor-pointer">Retry</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col min-h-0 overflow-hidden font-['Segoe_UI',sans-serif] ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>

      {/* ─── Top bar ─── */}
      <div className={`flex items-center justify-between px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-app-border bg-app-header-bg' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-bold">Positions</span>
          <span className={`text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>
            {summary.count} position{summary.count !== 1 ? 's' : ''}
          </span>
          {demoMode && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#2e2a1a] text-accent-yellow">DEMO</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {Object.entries(accountMargins).map(([id, acct]) => (
            acct && !acct.error && (
              <span key={id} className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>
                {acct.label || id}: <span className={`font-medium ${isDark ? 'text-accent-yellow' : 'text-yellow-600'}`}>{fmtINR(acct.used || 0)}</span>
              </span>
            )
          ))}
          <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>
            Premium: <span className="text-accent-green font-medium">{fmtINR(summary.totalPremium)}</span>
          </span>
          <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>
            Remaining: <span className={`font-medium ${isDark ? 'text-app-text' : 'text-gray-700'}`}>{fmtINR(summary.totalRemaining)}</span>
          </span>
          <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>
            Profit: <span className={`font-semibold ${summary.totalProfit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>{fmtINR(summary.totalProfit)}</span>
          </span>
          <button onClick={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
            className={`px-2 py-0.5 text-xs rounded border cursor-pointer ${isDark ? 'bg-app-input border-app-border-input text-app-text-muted' : 'bg-gray-100 border-gray-300 text-gray-500'}`}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* ─── Filter bar (search + range inputs only — checkbox filters are in column headers) ─── */}
      <div className={`flex items-center gap-2 px-4 py-1.5 border-b flex-shrink-0 flex-wrap ${isDark ? 'border-app-border bg-app-header-bg' : 'border-gray-200 bg-gray-50'}`}>
        <input type="text" placeholder="Search: reliance, pe, 1400..." value={search} onChange={e => setSearch(e.target.value)}
          className={`w-52 ${inputClass}`} />
        <span className={`text-[11px] ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>OI (lots) ≥</span>
        <input type="number" placeholder="Min" value={oiMin} onChange={e => setOiMin(e.target.value)}
          className={`w-16 text-center ${inputClass}`} />
        <span className={`text-[11px] ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Vol (lots) ≥</span>
        <input type="number" placeholder="Min" value={volMin} onChange={e => setVolMin(e.target.value)}
          className={`w-16 text-center ${inputClass}`} />
        <span className={`text-[11px] ml-1 ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Result:</span>
        <input type="date" value={resultDateFrom} onChange={e => setResultDateFrom(e.target.value)}
          className={`w-[120px] text-center ${inputClass}`} title="Result date from" />
        <span className={`text-[11px] ${isDark ? 'text-app-text-dim' : 'text-gray-400'}`}>–</span>
        <input type="date" value={resultDateTo} onChange={e => setResultDateTo(e.target.value)}
          className={`w-[120px] text-center ${inputClass}`} title="Result date to" />
        <button onClick={() => { setSortVersion(v => v + 1); if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current); autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000); }} title="Re-sort and refresh positions"
          className="px-2.5 py-0.5 text-[11px] font-semibold rounded border cursor-pointer bg-blue-500/15 text-blue-500 border-blue-500/30">
          ↻ Re-sort
        </button>
        {/* Columns manager — show/hide, drag-to-reorder, reset (also the touch-friendly control). Resize is via header grips. */}
        <ColumnsButton cols={cols} isDark={isDark} />
        <button onClick={() => downloadRowsAsCSV(rows, cols.columns, `positions_${new Date().toISOString().split('T')[0]}.csv`)} title="Download current view as CSV"
          className="px-2.5 py-0.5 text-[11px] font-semibold rounded border cursor-pointer bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
          ↓ CSV
        </button>
        {(search || typeFilter.length > 0 || bsFilter.length > 0 || publicPrivateFilter.length > 0 || accountFilter.length > 0 || sectorFilter.length > 0 || categoryFilter.length > 0 || expiryFilter.length > 0 || oiMin || volMin || resultDateFrom || resultDateTo) && (
          <button onClick={() => { setSearch(''); setTypeFilter([]); setBsFilter([]); setPublicPrivateFilter([]); setAccountFilter([]); setSectorFilter([]); setCategoryFilter([]); setExpiryFilter([]); setOiMin(''); setVolMin(''); setResultDateFrom(''); setResultDateTo(''); }}
            className="px-2.5 py-0.5 text-[10px] font-semibold rounded border cursor-pointer bg-red-500/15 text-red-500 border-red-500/30">
            Clear All Filters
          </button>
        )}
        <span className={`text-[11px] ml-auto ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>
          {fmtNum(rows.length)} position{rows.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ─── Table ─── */}
      <PositionsTable
        rows={rows}
        sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
        onTradeClick={handleTradeClick}
        isDark={isDark}
        columns={cols.columns}
        onColumnResize={cols.resize}
        ordersBySymbol={ordersBySymbol}
        filters={{
          expiryFilter, setExpiryFilter, uniqueExpiries,
          typeFilter, setTypeFilter,
          bsFilter, setBsFilter,
          publicPrivateFilter, setPublicPrivateFilter, uniquePublicPrivate,
          sectorFilter, setSectorFilter, uniqueSectors,
          categoryFilter, setCategoryFilter, uniqueCategories,
          accountFilter, setAccountFilter, uniqueAccountLabels,
        }}
        t={t}
      />

      {/* ─── Trade Popup (choice: Add More / Square Off) ─── */}
      {tradePopup && (
        <PositionsTradePopup
          row={tradePopup} isDark={isDark}
          onClose={() => setTradePopup(null)}
          onAddMore={handleAddMore}
          onSquareOff={handleSquareOff}
        />
      )}

      {/* ─── Order Popup (shared with MW — actual order entry) ─── */}
      {orderPopupRow && (
        <OrderPopup
          themeCtx={{ t, isDark, rowPad: '6px 8px', fontSize: 12.5 }}
          row={orderPopupRow}
          orderLots={orderLots} setOrderLots={setOrderLots}
          orderPrice={orderPrice} setOrderPrice={setOrderPrice}
          orderMarginData={orderMarginData}
          orderAccount={orderAccount}
          onAccountChange={onOrderAccountChange}
          accountMargins={accountMargins}
          margins={margins}
          onRefetchMargin={refetchOrderMargin}
          onClose={() => setOrderPopupRow(null)}
          mode={orderPopupMode}
          positionDirection={orderPopupDirection}
          user={user}
        />
      )}
    </div>
  );
}
